import { useEffect, useState } from "react";
import { config, renderTokens } from "@/config/eventConfig";
import { usePersistentState } from "@/hooks/usePersistentState";
import { submitWrap, flushWrapQueue } from "@/lib/wrapQueue";

const recipeName = (id) =>
  (config.recipes.find((r) => r.id === id) || {}).name || id || "-";

export const SaveWrap = () => {
  const [subject] = usePersistentState("build.subject", "");
  const [who] = usePersistentState("build.who", "");
  const [done] = usePersistentState("build.done", "");
  const [recipeId] = usePersistentState("build.recipeId", null);
  const [teamName] = usePersistentState("ps.teamName", "");

  const [org, setOrg] = usePersistentState("build.organization", "");
  const [tableLabel, setTableLabel] = usePersistentState("build.tableLabel", teamName || "");
  const [goodFor, setGoodFor] = usePersistentState("wrap.good_for", "");
  const [nextTest, setNextTest] = usePersistentState("wrap.next_test", "");
  const [presenter, setPresenter] = usePersistentState("wrap.presenter", "");
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    flushWrapQueue();
  }, []);

  const scoping = `We are building ${subject || "___"} for ${who || "___"}, and done looks like ${done || "___"}.`;

  const summary =
    `Table: ${tableLabel || "-"}\n` +
    `Organization: ${org || "-"}\n` +
    `Recipe: ${recipeName(recipeId)}\n` +
    `Scoping: ${scoping}\n` +
    `Good for: ${goodFor || "-"}\n` +
    `Next week we'll try: ${nextTest || "-"}\n` +
    `Presenting: ${presenter || "-"}`;

  const save = () => {
    const expireAt = new Date(
      new Date(config.event_date + "T00:00:00Z").getTime() +
        config.retention_hours * 3600 * 1000
    ).toISOString();
    // Fire-and-forget; never blocks the card. Presenter stays on-device.
    submitWrap({
      table_label: tableLabel,
      organization: org,
      recipe_id: recipeId || "",
      scoping_sentence: scoping,
      data_flag: "",
      good_for: goodFor,
      next_test: nextTest,
      expire_at: expireAt,
    });
    setSaved(true);
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const field = "w-full resize-none bg-white border border-sand focus:border-orange focus:outline-none px-3 py-2";

  return (
    <div data-testid="save-wrap">
      <p className="mono-label text-orange-dark mb-2">Optional</p>
      <p data-testid="wrap-optional-note" className="text-ink/70 mb-6 max-w-2xl">
        This is the only place you type, and nothing depends on it. Fill it in
        if you want your team to leave with its own summary, or skip it.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block mb-1 mono-label text-ink/60">Table label</label>
          <input data-testid="wrap-table" value={tableLabel} onChange={(e) => setTableLabel(e.target.value)} className="w-full bg-white border border-sand focus:border-orange focus:outline-none px-3 py-2" />
        </div>
        <div>
          <label className="block mb-1 mono-label text-ink/60">Organization</label>
          {config.organizations && config.organizations.length > 0 ? (
            <select data-testid="wrap-org" value={org} onChange={(e) => setOrg(e.target.value)} className="w-full bg-white border border-sand focus:border-orange focus:outline-none px-3 py-2">
              <option value="">Select…</option>
              {config.organizations.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : (
            <input data-testid="wrap-org" value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Your organization" className="w-full bg-white border border-sand focus:border-orange focus:outline-none px-3 py-2" />
          )}
        </div>
      </div>

      <label className="block mb-1 font-semibold">What is this good for?</label>
      <textarea data-testid="wrap-good-for" rows={2} value={goodFor} onChange={(e) => setGoodFor(e.target.value)} className={`${field} mb-5`} />

      <label className="block mb-1 font-semibold">The one thing we will try with it in the next week</label>
      <textarea data-testid="wrap-next-test" rows={2} value={nextTest} onChange={(e) => setNextTest(e.target.value)} className={`${field} mb-5`} />

      <label className="block mb-1 font-semibold">Who is presenting, and what are they showing?</label>
      <textarea data-testid="wrap-presenter" rows={2} value={presenter} onChange={(e) => setPresenter(e.target.value)} className={field} />

      <button data-testid="wrap-save" onClick={save} className="mt-6 px-6 py-3 bg-orange hover:bg-orange-dark text-white transition-colors duration-150">
        Save and wrap
      </button>

      {saved && (
        <div data-testid="wrap-summary" className="mt-8 bg-white border border-sand p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="mono-label text-ink/50">Your wrap</span>
            <button data-testid="wrap-copy-all" onClick={copyAll} className="text-sm border border-ink/20 px-4 py-1.5 hover:border-orange hover:text-orange-dark transition-colors duration-150">
              {copied ? "Copied" : "Copy all"}
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-body text-ink/80">{summary}</pre>
          <p className="callout-guidance text-teal mt-5">
            {renderTokens("Note Taker: paste this into your organization's {assistant.project_noun} before you close the laptop.")}
          </p>
        </div>
      )}
    </div>
  );
};
